import React from 'react';
import { FaBriefcase, FaBullhorn, FaLock, FaPassport, FaShieldHalved } from 'react-icons/fa6';
import { APPLICATION_FIELDS, DISCLOSURE_FIELDS } from '../utils/applicationProfile';
import './ApplicationProfileFields.css';

const SECTIONS = [
  { id: 'eligibility', title: 'Work eligibility', description: 'Work authorization and sponsorship', Icon: FaPassport,
    fields: ['authorized_to_work_us', 'requires_visa_sponsorship', 'at_least_18', 'us_work_status'] },
  { id: 'preferences', title: 'Job preferences', description: 'Where and when you would like to work', Icon: FaBriefcase,
    fields: ['willing_to_relocate', 'start_availability', 'salary_expectations'] },
  { id: 'background', title: 'Employment background', description: 'Details that may matter for your next role', Icon: FaShieldHalved,
    fields: ['has_employment_restrictions', 'has_us_federal_or_military_employment'] },
];

const FIELD_PRESENTATION = {
  us_work_status: { wide: true, placeholder: 'Select your work status' },
  start_availability: { placeholder: 'e.g. Two weeks after an offer' },
  salary_expectations: { placeholder: 'e.g. $90,000–$110,000 / year' },
  has_employment_restrictions: { title: 'Non-compete or non-solicitation restrictions' },
  has_us_federal_or_military_employment: { title: 'U.S. government or military employment' },
  how_did_you_hear_about_job: { placeholder: 'e.g. LinkedIn, a referral, or a career fair' },
};

const ApplicationSection = ({ id, title, description, Icon, optional = false, children }) => (
  <section className="application-answer-section" aria-labelledby={`${id}-heading`}>
    <div className="application-section-heading">
      <span className="application-section-icon" aria-hidden="true"><Icon /></span>
      <div className="application-section-copy">
        <h6 id={`${id}-heading`}>{title}</h6>
        <p>{description}</p>
      </div>
      {optional && <span className="application-optional-badge">Optional</span>}
    </div>
    <div className="application-answer-grid">{children}</div>
  </section>
);

const ApplicationProfileFields = ({ value, onChange, errors = {}, disclosures = false, jobSource = false, required = false, disabled = false, idPrefix = 'application' }) => {
  const renderField = (field, voluntary = false) => {
    const name = voluntary ? `voluntary_disclosures.${field.name}` : field.name;
    const current = voluntary ? value.voluntary_disclosures?.[field.name] : value[field.name];
    const id = `${idPrefix}-${field.name}`;
    const presentation = FIELD_PRESENTATION[field.name] || {};
    const error = errors[name];
    const update = next => onChange(voluntary
      ? { ...value, voluntary_disclosures: { ...value.voluntary_disclosures, [field.name]: next } }
      : { ...value, [field.name]: next });
    const requiredMark = required && field.required && <span className="application-required-mark"> *</span>;
    const errorMessage = error && <div id={`${id}-error`} className="application-field-error" role="alert">{error}</div>;

    if (field.type === 'boolean') {
      return (
        <fieldset key={name} className="application-answer-field application-answer-field--boolean" disabled={disabled} aria-invalid={Boolean(error)} aria-describedby={[presentation.title ? `${id}-description` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined}>
          <legend className="application-visually-hidden">{field.label}</legend>
          <div className="application-question-row">
            <div className="application-answer-copy">
              <div className="application-answer-label">{presentation.title || field.label}{requiredMark}</div>
              {presentation.title && <p id={`${id}-description`} className="application-answer-description">{field.label}</p>}
            </div>
            <div className="application-answer-controls">
              <div className="application-answer-choices">
                {[[true, 'Yes'], [false, 'No']].map(([answer, label]) => (
                  <div className="application-answer-choice" key={label}>
                    <input
                      id={`${id}-${label.toLowerCase()}`}
                      className="application-choice-input"
                      type="radio"
                      name={id}
                      value={String(answer)}
                      checked={current === answer}
                      onChange={() => update(answer)}
                      aria-required={Boolean(required && field.required)}
                      aria-invalid={Boolean(error)}
                    />
                    <label htmlFor={`${id}-${label.toLowerCase()}`}>{label}</label>
                  </div>
                ))}
              </div>
              <button type="button" className="application-answer-reset" aria-label={`Clear answer: ${field.label}`} disabled={disabled || current === null || current === undefined} onClick={() => update(null)}>Clear</button>
            </div>
          </div>
          {errorMessage}
        </fieldset>
      );
    }

    const selected = current === null || current === undefined ? '' : String(current);
    return (
      <div className={`application-answer-field${presentation.wide || field.name === 'how_did_you_hear_about_job' ? ' application-answer-field--wide' : ''}`} key={name}>
        <label className="application-answer-label" htmlFor={id}>{field.label}{requiredMark}</label>
        {field.options ? (
          <select id={id} value={selected} onChange={event => update(event.target.value || null)} disabled={disabled} className="form-select application-answer-input" aria-required={Boolean(required && field.required)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined}>
            <option value="">{presentation.placeholder || 'Select an option'}</option>
            {selected && !field.options.some(([code]) => code === selected) && <option value={selected}>{selected}</option>}
            {field.options.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        ) : (
          <input id={id} type="text" value={current ?? ''} onChange={event => update(event.target.value || null)} maxLength={field.maxLength} placeholder={presentation.placeholder} disabled={disabled} className="form-control application-answer-input" aria-required={Boolean(required && field.required)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} />
        )}
        {errorMessage}
      </div>
    );
  };
  return (
    <div className="application-profile-fields">
      {SECTIONS.map(section => (
        <ApplicationSection key={section.id} {...section} id={`${idPrefix}-${section.id}`}>
          {section.fields.map(name => renderField(APPLICATION_FIELDS.find(field => field.name === name)))}
        </ApplicationSection>
      ))}
      {jobSource && (
        <ApplicationSection id={`${idPrefix}-source`} title="About this application" description="Tell us where you found this opportunity" Icon={FaBullhorn}>
          {renderField({ name: 'how_did_you_hear_about_job', label: 'How did you hear about this job?', maxLength: 255 })}
        </ApplicationSection>
      )}
      {disclosures && (
        <ApplicationSection id={`${idPrefix}-disclosures`} title="Voluntary disclosures" description="These answers are optional and are not sent with job applications." Icon={FaLock} optional>
          {DISCLOSURE_FIELDS.map(field => renderField(field, true))}
        </ApplicationSection>
      )}
    </div>
  );
};

export default ApplicationProfileFields;
